import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Github,
  Linkedin,
  Mail,
  Code2,
  Figma,
  Database,
  Braces,
  Palette,
  Flame,
} from 'lucide-react';

const skills = [
  { name: 'Tailwind CSS', icon: Palette },
  { name: 'Javascript', icon: Braces },
  { name: 'Unity', icon: Flame },
  { name: 'Python', icon: Code2 },
  { name: 'PHP', icon: Code2 },
  { name: 'MongoDB', icon: Database },
  { name: 'Node.js', icon: Braces },
  { name: 'AI Machine Learning', icon: Flame },
  { name: 'React.js', icon: Code2 },
  { name: 'MySQL', icon: Database },
  { name: 'BullMQ', icon: Braces },
  { name: 'Laravel', icon: Code2 },
  { name: 'Redis', icon: Database },
  { name: 'Firebase', icon: Flame },
  { name: 'Figma', icon: Figma },
  { name: 'Render', icon: Code2 },
  { name: 'WordPress', icon: Code2 },
  { name: 'Object-Oriented Programming', icon: Code2 },
];

const caseStudies = [
  {
    title: "AI Scheduler",
    category: "Product Concept",
    description:
      "A smart scheduling assistant concept designed to make planning, reminders and calendar management feel effortless.",
    tags: ["AI", "UX", "Prototype"],
    href: "/case-studies/ai-scheduler",
  },
  {
    title: "TherapEase",
    category: "UX/UI Capstone",
    description:
      "A 5‑month thesis MERN application that consolidates scheduling, client records, payments and communications for therapists. Built with a focus on usability, automation (Stripe, Zoom, 2FA) and measurable workflow improvements.",
    tags: ["MERN", "Stripe", "UX", "Automation", "Chart.js", "2FA", "Zoom"],
    href: "/case-studies/therapease",
  },
  {
    title: "CampusPost",
    category: "Product Concept",
    description:
      "A student-centred social platform concept for campus communities, events, posts and connection.",
    tags: ["Social", "UI", "Frontend"],
    href: "/case-studies/campuspost",
  },
];

function SkillCarousel() {
  const repeatedSkills = [...skills, ...skills];

  return (
    <div className="relative mt-14 w-full overflow-hidden border-y border-white/10 py-5">
      <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-28 bg-gradient-to-r from-[#240711] to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-28 bg-gradient-to-l from-[#240711] to-transparent" />

      <motion.div
        className="flex w-max gap-4"
        animate={{ x: [0, -960] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
      >
        {repeatedSkills.map((skill, index) => {
          const Icon = skill.icon;

          return (
            <div
              key={`${skill.name}-${index}`}
              className="flex items-center gap-3 rounded-full border border-emerald-200/20 bg-white/5 px-6 py-3 text-sm font-medium text-white/80 shadow-lg backdrop-blur"
            >
              <Icon size={18} className="text-emerald-200" />
              <span>{skill.name}</span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}

function CaseStudyShowcase() {
  return (
    <section id="case-studies" className="relative z-20 mx-auto mt-24 max-w-7xl px-6 pb-28">
      <div className="mb-12 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.45em] text-emerald-100/70">
          Selected work
        </p>
        <h2 className="mt-4 text-4xl font-black tracking-tight text-white md:text-6xl">
          Case Studies
        </h2>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {caseStudies.map((project, index) => (
          <motion.a
            key={project.title}
            href={project.href}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.15 + index * 0.12,
              duration: 0.7,
              ease: "easeOut",
            }}
            whileHover={{
              y: -18,
              scale: 1.03,
            }}
            className="group relative min-h-[430px] overflow-hidden rounded-[2rem] border border-white/15 bg-[#4a1021] p-7 shadow-2xl shadow-black/40 transition"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(167,243,208,0.22),transparent_42%)]"/>
            <div className="absolute bottom-0 right-0 h-44 w-44 rounded-full bg-emerald-200/10 blur-3xl" />

            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className="mb-10 flex items-start justify-between gap-5">
                  <span className="rounded-full border border-emerald-100/25 bg-white/10 px-4 py-2 font-mono text-[0.65rem] font-bold uppercase tracking-[0.24em] text-emerald-100">
                    {project.category}
                  </span>

                  <ArrowUpRight className="text-emerald-100 transition duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>

                <h3 className="text-4xl font-black tracking-tight text-white md:text-5xl">
                  {project.title}
                </h3>

                <p className="mt-5 text-base leading-7 text-white/70">
                  {project.description}
                </p>
              </div>

              <div>
               

                <div className="mb-7 flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-black/25 px-3 py-1 text-xs font-semibold text-white/75"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-3 font-semibold text-emerald-100">
                  View case study
                  <span className="h-px w-10 bg-emerald-100 transition-all duration-300 group-hover:w-20" />
                </div>
              </div>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#240711] text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-[-10rem] top-[-10rem] h-[32rem] w-[32rem] rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="absolute bottom-[-12rem] right-[-12rem] h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#4f1226]/60 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px]" />
      </div>

      <nav className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-7">
        <a href="/" className="font-mono text-sm tracking-[0.35em] text-emerald-100">AL</a>

        <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 items-center gap-8 text-sm text-white/65">
          <a href="#case-studies" className="transition hover:text-emerald-100">case studies</a>
          <a href="#skills" className="transition hover:text-emerald-100">skills</a>
          <a href="#contact" className="transition hover:text-emerald-100">contact</a>
        </div>

        <div className="flex items-center gap-3">
          <a href="https://github.com/aoifelynch" className="rounded-full border border-white/10 bg-white/5 p-3 text-white/70 transition hover:border-emerald-200/40 hover:text-emerald-100"><Github size={18} /></a>
          <a href="https://www.linkedin.com/in/aoife-lynch-3687401ba/" className="rounded-full border border-white/10 bg-white/5 p-3 text-white/70 transition hover:border-emerald-200/40 hover:text-emerald-100"><Linkedin size={18} /></a>
          <a href="mailto:aoifellynch@outlook.com" className="rounded-full border border-white/10 bg-white/5 p-3 text-white/70 transition hover:border-emerald-200/40 hover:text-emerald-100"><Mail size={18} /></a>
        </div>
      </nav>

      <section className="relative z-10 flex min-h-[calc(100vh-96px)] flex-col justify-center pb-16 pt-10">
        <div className="mx-auto w-full max-w-7xl px-6 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-5 font-mono text-sm uppercase tracking-[0.45em] text-emerald-100/80">Portfolio 2026</motion.p>

          <motion.h1 initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.8, ease: 'easeOut' }} className="mx-auto max-w-7xl text-[clamp(4.7rem,16vw,15rem)] font-black uppercase leading-[0.78] tracking-[-0.09em] text-white">Aoife Lynch</motion.h1>

          <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }} className="mx-auto mt-8 flex w-fit items-center gap-4 rounded-full border border-emerald-200/20 bg-white/5 px-6 py-3 backdrop-blur">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-200 shadow-[0_0_20px_rgba(167,243,208,0.9)]" />
            <p className="font-mono text-sm uppercase tracking-[0.3em] text-emerald-50">Frontend Developer</p>
          </motion.div>
        </div>

        <section id="skills">
          <SkillCarousel />
        </section>

        <CaseStudyShowcase />

        
      </section>
    </main>
  );
}
